var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { TASK_ORDER_STATUS_SELECT_ID } from 'ziphy-web-shared/basic/entities/notes/definitions';
import { useLoader } from 'ziphy-web-shared/basic/hooks';
import { Button } from 'ziphy-web-shared/basic/lib/buttons';
import { useMobxForm } from 'ziphy-web-shared/basic/lib/forms';
import { Select, Textarea } from 'ziphy-web-shared/basic/lib/forms/fields';
import { useDefinitionSelectItems } from 'ziphy-web-shared/basic/lib/forms/fields/select/Select.hooks';
import { Title } from 'ziphy-web-shared/basic/lib/simple';
import { Modal, ModalToolbarBtn } from 'ziphy-web-shared/basic/lib/utilities/modals';
import { $loader } from 'ziphy-web-shared/basic/utils';
import { createCodeableConcept, getFhirRef, getResourceProperties, isEnteredInErrorStatus, } from '../';
import SearchResourceInput from '../lib/SearchResourceInput';
import styles from './TaskOrder.modal.module.scss';
const TaskOrderModal = ({ encounterId, resourceContext, resourceId: _resourceId, searchTaxonomy, canChangeCode = false, title, _core, }) => {
    const { t } = useTranslation();
    const [data, setData] = useState();
    const [resourceId, setResourceId] = useState(_resourceId);
    const isEdit = useMemo(() => Boolean(_resourceId), [_resourceId]);
    const isDisabled = useMemo(() => (data === null || data === void 0 ? void 0 : data.status) === 'completed', [data === null || data === void 0 ? void 0 : data.status]);
    const properties = useMemo(() => getResourceProperties(resourceContext), [resourceContext]);
    const { service } = properties;
    const extractor = new properties.extractor(data);
    const isImaging = (resourceContext === null || resourceContext === void 0 ? void 0 : resourceContext.element) === 'order.imaging';
    const { isLoading } = useLoader(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!resourceId) {
            if (searchTaxonomy)
                setData({ orderDetail: [createCodeableConcept(searchTaxonomy)] });
            return;
        }
        const response = yield service.read({
            encounterId,
            fhirRef: getFhirRef(resourceContext.resourceType, resourceId),
            elementName: resourceContext.element,
        });
        if (!response.error) {
            setData(response.prepared);
        }
    }), [resourceId]);
    const form = useMobxForm(useMemo(() => ({
        initialValues: data,
        fieldsConfig: {
            orderDetail: { validate: ['required'] },
            note: { transformOut: ['emptyNull'] },
        },
    }), [data]));
    const codes = form.group('orderDetail', {}, {
        type: 'field',
        fieldConfig: { validate: ['required'] },
    });
    if (codes.value.length === 0)
        codes.add();
    const note = form.field('note', '');
    const status = form.field('status', 'draft');
    const isUpdate = Boolean(resourceId);
    const handleSubmit = form.submit((data) => __awaiter(void 0, void 0, void 0, function* () {
        const payload = {
            encounterId,
            elementName: resourceContext.element,
            data: data,
        };
        if (isUpdate) {
            payload.data.id = resourceId;
            const response = yield service.save(payload);
            if (!response.error) {
                yield _core.onHide(true, {
                    resourceId: response.prepared.id,
                    action: isEnteredInErrorStatus(data.status) ? 'DELETE' : 'UPDATE',
                });
            }
        }
        else {
            const response = yield service.create(payload);
            if (!response.error) {
                yield _core.onHide(true, {
                    resourceId: response.prepared.id,
                    action: 'CREATE',
                });
            }
        }
    }));
    const statusItems = useDefinitionSelectItems(TASK_ORDER_STATUS_SELECT_ID);
    return (_jsxs(Modal, Object.assign({ size: 'xl', centered: true, scrollable: true }, { children: [_jsx("div", Object.assign({ className: "modal-toolbar" }, { children: _jsx(ModalToolbarBtn, { type: "close", action: _core.onHide }) })), _jsx(Modal.Header, { children: title || isEdit
                    ? // ? joinValuesToString([t('label.order'), extractor?.status?.title], ' • ')
                        t('label.order')
                    : t('label.add_order') }), _jsx(Modal.Body, Object.assign({ isLoading: isLoading }, { children: _jsx("form", Object.assign({ id: form.id, onSubmit: $loader.registerHandler(form.id, handleSubmit) }, { children: _jsxs("div", Object.assign({ className: "row row--gutters-5 row--mb-10" }, { children: [status.value && (_jsx("div", Object.assign({ className: "col-12 col-md-4" }, { children: _jsx(Select, { field: status, mode: ['sm'], items: statusItems, label: t('label.status'), placeholder: t('label.select_status'), disabled: true }) }))), _jsxs("div", Object.assign({ className: "col-12" }, { children: [_jsx(Title, Object.assign({ size: 'h6', className: styles.title }, { children: isImaging ? t('label.imaging') : t('label.labs') })), codes.value.map((code, index) => (_jsxs("div", Object.assign({ className: styles.searchRow }, { children: [_jsx(SearchResourceInput, { field: code, errorMessage: code.error, 
                                                // setResourceId={setResourceId}
                                                encounterId: encounterId, resourceContext: Object.assign(Object.assign({}, resourceContext), { searchPreference: Object.assign(Object.assign({}, resourceContext.searchPreference), { resources: false }) }), placeholder: t('label.search_by_name_or_code'), mode: ['sm'], disabled: isDisabled, clear: !isDisabled, wrapperClassname: styles.field }), !isDisabled && codes.value.length > 1 && (_jsx(Button, { mode: ['secondary-icon', 'sm', 'justIcon'], leftElement: 'trash', onClick: () => codes.remove(index) }))] }), code.id))), !isDisabled && (_jsx(Button, Object.assign({ mode: ['secondary-dark', 'sm', 'block'], leftElement: 'plus', onClick: () => codes.add(), className: 'mt-10' }, { children: t('btn.add') })))] })), _jsx("div", Object.assign({ className: "col-12" }, { children: _jsx(Textarea, { field: note, mode: ['sm'], label: t('label.note'), placeholder: t('label.any_details'), rows: 5 }) }))] })) })) })), _jsxs(Modal.Footer, Object.assign({ className: 'gap-10' }, { children: [_jsx(Button, Object.assign({ mode: ['primary', 'sm', 'min110'], form: form.id, isLoading: $loader.isRunHandler(form.id), disabled: isLoading }, { children: t('btn.save') })), _jsx(Button, Object.assign({ mode: ['secondary', 'sm', 'min110'], disabled: isLoading, onClick: () => _core.onHide() }, { children: t('btn.cancel') }))] }))] })));
};
TaskOrderModal.displayName = 'TaskOrderModal';
export default observer(TaskOrderModal);
